import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { getSiteContext } from '../data/siteContext';

const { FaRobot, FaTimes, FaPaperPlane, FaUser, FaCommentDots } = Icons;

// Helper to render icons safely
const Icon = ({ Component, ...props }: { Component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ChatBotProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

const ChatBot: React.FC<ChatBotProps> = ({ isOpen, onClose, onOpen }) => {
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: language === 'tr' 
        ? 'Merhaba! Ben Furkan Gül\'ün AI asistanıyım. Furkan hakkında bilgi almak veya onu işe almak için sorularınızı sorabilirsiniz. Size nasıl yardımcı olabilirim?'
        : 'Hello! I\'m Furkan Gül\'s AI assistant. You can ask me questions about Furkan or about hiring him. How can I help you?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Language değiştiğinde welcome mesajını güncelle
  useEffect(() => {
    if (messages.length === 1 && messages[0].id === '1') {
      setMessages([{
        id: '1',
        text: language === 'tr' 
          ? 'Merhaba! Ben Furkan Gül\'ün AI asistanıyım. Furkan hakkında bilgi almak veya onu işe almak için sorularınızı sorabilirsiniz. Size nasıl yardımcı olabilirim?'
          : 'Hello! I\'m Furkan Gül\'s AI assistant. You can ask me questions about Furkan or about hiring him. How can I help you?',
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const generateResponse = async (userMessage: string): Promise<string> => {
    const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY || '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';
    const siteContext = getSiteContext(language);
    const isEnglish = language === 'en';

    // Conversation history'yi API formatına çevir (welcome mesajı hariç)
    const conversationHistory = messages
      .filter(msg => !(msg.id === '1' && msg.sender === 'bot')) // İlk welcome mesajını hariç tut
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

    // OpenRouter API kullan
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Furkan Gül CV Website'
        },
        body: JSON.stringify({
          model: 'google/gemma-3n-e4b-it:free',
          messages: [
            {
              role: 'system',
              content: `Sen Furkan Gül'ün profesyonel CV asistanısın. ${isEnglish ? 'Always respond in English.' : 'Her zaman Türkçe yanıt ver.'} Kullanıcıların sorularını YUKARIDAKİ BİLGİLERİ KULLANARAK doğrudan ve detaylı yanıtla.

${siteContext}

ÖNEMLİ KURALLAR:
1. YUKARIDAKİ BİLGİLERİ KULLAN: Tüm soruları yukarıdaki context'teki bilgileri kullanarak yanıtla
2. DOĞRUDAN YANIT VER: Sorulara direkt cevap ver, genel mesajlar yazma
3. SPESİFİK OL: Detaylı bilgiler ver, örnekler kullan
4. BAĞLAM TAKİBİ: Önceki mesajları hatırla ve tutarlı yanıtlar ver

SORU TİPLERİNE ÖRNEKLER:
- "junior mu?" → Hayır, mid-level. 2022'den beri profesyonel deneyim, 7+ production projesi, SOFTİTO birinciliği
- "hangi üniversite?" → Adana Alparslan Türkeş Bilim ve Teknolojisi Üniversitesi, Bilgisayar Mühendisliği (2017-2023)
- "deneyim ne kadar?" → 2022'den beri profesyonel: FMSS (2022), Tech Career Bootcamp (2023-2024), Sistem Network Uzman Yardımcısı (2024), Freelance (2024-devam)
- "hangi teknolojiler?" → .NET Core, React.js, Java Spring Boot, PostgreSQL, Docker, Azure, AWS, SQL Server
- "projeler?" → Pastacı Hamdi, Pace, Webspider, Sisore Restaurant, Bijusaray, Payitaht İnşaat, Eloboost (production projeleri)

HER ZAMAN:
- Spesifik tarihler, şirket isimleri, teknolojiler kullan
- Furkan'ın başarılarını vurgula (SOFTİTO birinciliği, production projeleri)
- İletişim bilgilerini paylaş: furkan@furkangul.net, +90 507 584 77 22`
            },
            ...conversationHistory,
            {
              role: 'user',
              content: userMessage
            }
          ],
          temperature: 0.7,
          max_tokens: 800
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.choices && data.choices[0] && data.choices[0].message) {
          return data.choices[0].message.content;
        }
      } else {
        // Response hatası - detaylı log
        const errorText = await response.text();
        console.error('OpenRouter API error response:', response.status, errorText);
        // Fallback to static responses
      }
    } catch (error) {
      console.error('OpenRouter API error:', error);
      // Fallback to static responses
    }

    // Fallback: Static responses (OpenRouter key yoksa veya hata olursa)
    const lowerMessage = userMessage.toLowerCase();

    // Junior/Mid/Senior soruları
    if (lowerMessage.includes('junior') || lowerMessage.includes('mid') || lowerMessage.includes('senior') || lowerMessage.includes('seviye') || lowerMessage.includes('level')) {
      return isEnglish
        ? `Furkan Gül is a mid-level Full Stack Developer. He has professional experience since 2022 (FMSS, Tech Career Bootcamp, System Network Specialist, and Freelance). He has 7+ production projects, won 1st place in SOFTİTO Backend Bootcamp (2025), and has been working as a freelancer since January 2024. He is suitable for mid-level positions and can take on technical leadership roles.`
        : `Furkan Gül mid-level bir Full Stack Developer. 2022'den beri profesyonel deneyime sahip (FMSS, Tech Career Bootcamp, Sistem Network Uzman Yardımcısı ve Freelance). 7+ production projesi var, SOFTİTO Backend Bootcamp'te birincilik kazandı (2025) ve Ocak 2024'ten beri freelance olarak çalışıyor. Mid-level pozisyonlar için uygun ve teknik liderlik rollerini üstlenebilir.`;
    }

    // Üniversite soruları
    if (lowerMessage.includes('üniversite') || lowerMessage.includes('okul') || lowerMessage.includes('mezun') || lowerMessage.includes('university') || lowerMessage.includes('school') || lowerMessage.includes('graduate')) {
      return isEnglish
        ? `Furkan Gül graduated from Adana Alparslan Türkeş Science and Technology University (ATU) with a Bachelor's degree in Computer Engineering (2017-2023). Website: www.atu.edu.tr. He also won 1st place in Istanbul Chamber of Commerce's SOFTİTO Backend Bootcamp (2025, 320 hours).`
        : `Furkan Gül, Adana Alparslan Türkeş Bilim ve Teknolojisi Üniversitesi'nden (ATÜ) Bilgisayar Mühendisliği lisans derecesi ile mezun oldu (2017-2023). Website: www.atu.edu.tr. Ayrıca İstanbul Ticaret Odası'nın SOFTİTO Backend Bootcamp programında birincilik kazandı (2025, 320 saat).`;
    }

    // Deneyim süresi soruları
    if (lowerMessage.includes('experience') || lowerMessage.includes('tecrübe') || lowerMessage.includes('deneyim') || lowerMessage.includes('kaç yıl') || lowerMessage.includes('ne kadar')) {
      return isEnglish 
        ? `Furkan Gül has been working professionally since 2022. Experience timeline: FMSS - Full Stack Developer (June-September 2022), Tech Career Fullstack Bootcamp (December 2023-January 2024), System Network Assistant Specialist (March-September 2024), and Freelance Full Stack Developer (January 2024 - Present). He has 7+ production projects and won 1st place in SOFTİTO Backend Bootcamp (2025).`
        : `Furkan Gül 2022'den beri profesyonel olarak çalışıyor. Deneyim zaman çizelgesi: FMSS - Full Stack Developer (Haziran-Eylül 2022), Tech Career Fullstack Bootcamp (Aralık 2023-Ocak 2024), Sistem Network Uzman Yardımcısı (Mart-Eylül 2024) ve Freelance Full Stack Developer (Ocak 2024 - Devam Ediyor). 7+ production projesi var ve SOFTİTO Backend Bootcamp'te birincilik kazandı (2025).`;
    }

    if (lowerMessage.includes('education') || lowerMessage.includes('eğitim') || lowerMessage.includes('softito') || lowerMessage.includes('bootcamp')) {
      return isEnglish
        ? `Furkan Gül graduated from Adana Alparslan Türkeş Science and Technology University (ATU) with a Bachelor's degree in Computer Engineering (2017-2023). Website: www.atu.edu.tr. He achieved 1st place 🏆 in Istanbul Chamber of Commerce's SOFTİTO Backend Bootcamp (2025, 320 hours), covering C# .NET, ASP.NET Core, Entity Framework Core, SQL Server, RESTful API development, Layered Architecture, Clean Architecture, Identity for user management, Docker, Git/GitHub, and microservices fundamentals.`
        : `Furkan Gül, Adana Alparslan Türkeş Bilim ve Teknolojisi Üniversitesi'nden (ATÜ) Bilgisayar Mühendisliği lisans derecesi ile mezun oldu (2017-2023). Website: www.atu.edu.tr. İstanbul Ticaret Odası'nın SOFTİTO Backend Bootcamp programında birincilik 🏆 elde etti (2025, 320 saat), C# .NET, ASP.NET Core, Entity Framework Core, SQL Server, RESTful API geliştirme, Katmanlı Mimari, Clean Architecture, Identity ile kullanıcı yönetimi, Docker, Git/GitHub ve mikroservis temelleri konularını kapsadı.`;
    }

    if (lowerMessage.includes('technology') || lowerMessage.includes('teknoloji') || lowerMessage.includes('skill') || lowerMessage.includes('beceri')) {
      return isEnglish
        ? `Furkan's technical skills include: Backend (.NET, C#, ASP.NET Core, Entity Framework, SQL Server, PostgreSQL, MongoDB, RESTful API, Microservices, Docker), Java (Spring Boot, Spring Security, Hibernate, JPA), Frontend (React, React.js, JavaScript, TypeScript, HTML5, CSS3, SCSS, Redux), DevOps (Git, GitHub, AWS, Azure, Linux), and Software Engineering principles (SOLID, Clean Architecture, OOP, Unit Testing).`
        : `Furkan'ın teknik becerileri: Backend (.NET, C#, ASP.NET Core, Entity Framework, SQL Server, PostgreSQL, MongoDB, RESTful API, Microservices, Docker), Java (Spring Boot, Spring Security, Hibernate, JPA), Frontend (React, React.js, JavaScript, TypeScript, HTML5, CSS3, SCSS, Redux), DevOps (Git, GitHub, AWS, Azure, Linux) ve Yazılım Mühendisliği prensipleri (SOLID, Clean Architecture, OOP, Unit Testing).`;
    }

    if (lowerMessage.includes('job') || lowerMessage.includes('position') || lowerMessage.includes('iş') || lowerMessage.includes('pozisyon')) {
      return isEnglish
        ? `Furkan is suitable for positions such as: Backend Developer, Full Stack Developer, .NET Developer, API Developer, DevOps Engineer, System Administrator, and Technical Lead. He can lead backend-focused projects and provide team coordination. Currently available for freelance projects and full-time positions.`
        : `Furkan şu pozisyonlar için uygundur: Backend Developer, Full Stack Developer, .NET Developer, API Developer, DevOps Engineer, System Administrator ve Technical Lead. Backend odaklı projelerde liderlik yapabilir ve takım koordinasyonu sağlayabilir. Şu anda freelance projeler ve tam zamanlı pozisyonlar için müsait.`;
    }

    if (lowerMessage.includes('project') || lowerMessage.includes('github') || lowerMessage.includes('proje')) {
      return isEnglish
        ? `Furkan has developed various projects including freelance production projects: Pastacı Hamdi, Pace, Webspider, Sisore Restaurant, Bijusaray, Payitaht Construction, Eloboost. He also has open-source projects on GitHub (github.com/furkan1287). Projects showcase his skills in .NET, React.js, and full-stack development.`
        : `Furkan, freelance production projeleri dahil çeşitli projeler geliştirdi: Pastacı Hamdi, Pace, Webspider, Sisore Restaurant, Bijusaray, Payitaht İnşaat, Eloboost. Ayrıca GitHub'da açık kaynak projeleri var (github.com/furkan1287). Projeler .NET, React.js ve full-stack geliştirme becerilerini sergiliyor.`;
    }

    if (lowerMessage.includes('location') || lowerMessage.includes('konum') || lowerMessage.includes('nerede') || lowerMessage.includes('yaşıyor') || lowerMessage.includes('yaşadığı') || lowerMessage.includes('şehir') || lowerMessage.includes('city')) {
      return isEnglish
        ? `Furkan Gül is based in Istanbul, Turkey. He is available for freelance projects and full-time positions, and can work remotely or on-site in Istanbul.`
        : `Furkan Gül İstanbul, Türkiye'de yaşıyor. Freelance projeler ve tam zamanlı pozisyonlar için müsait, uzaktan veya İstanbul'da ofiste çalışabilir.`;
    }

    if (lowerMessage.includes('contact') || lowerMessage.includes('email') || lowerMessage.includes('iletişim')) {
      return isEnglish
        ? `Contact Furkan: Email - furkan@furkangul.net, Phone - +90 507 584 77 22, GitHub - github.com/furkan1287, LinkedIn - linkedin.com/in/furkan1287. Based in Istanbul, Turkey. Available for freelance projects and full-time positions.`
        : `Furkan ile iletişim: Email - furkan@furkangul.net, Telefon - +90 507 584 77 22, GitHub - github.com/furkan1287, LinkedIn - linkedin.com/in/furkan1287. İstanbul, Türkiye merkezli. Freelance projeler ve tam zamanlı pozisyonlar için müsait.`;
    }

    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('merhaba') || lowerMessage.includes('selam')) {
      return isEnglish
        ? `Hello! I'm Furkan Gül's AI assistant. Furkan is a mid-level Full Stack Developer and DevOps Engineer. He won 1st place in SOFTİTO Backend Bootcamp (2025), has 7+ production projects, and has been working as a freelancer since January 2024. He has experience with .NET Core, React.js, Java Spring Boot, PostgreSQL, Docker, Azure, and AWS. How can I help you learn more about him?`
        : `Merhaba! Ben Furkan Gül'ün AI asistanıyım. Furkan, mid-level bir Full Stack Developer ve DevOps Engineer. SOFTİTO Backend Bootcamp'te birincilik kazandı (2025), 7+ production projesi var ve Ocak 2024'ten beri freelance olarak çalışıyor. .NET Core, React.js, Java Spring Boot, PostgreSQL, Docker, Azure ve AWS konularında deneyimli. Hakkında daha fazla bilgi almak için size nasıl yardımcı olabilirim?`;
    }

    // Genel yanıt - daha bilgilendirici
    return isEnglish
      ? `I can provide detailed information about Furkan Gül. He is a mid-level Full Stack Developer with experience since 2022. He graduated from Adana Alparslan Türkeş Science and Technology University (Computer Engineering, 2017-2023), won 1st place in SOFTİTO Backend Bootcamp (2025), and has 7+ production projects. He works with .NET Core, React.js, Java Spring Boot, PostgreSQL, Docker, Azure, and AWS. What specific information would you like to know? Contact: furkan@furkangul.net, +90 507 584 77 22`
      : `Furkan Gül hakkında detaylı bilgi verebilirim. 2022'den beri deneyime sahip mid-level bir Full Stack Developer. Adana Alparslan Türkeş Bilim ve Teknolojisi Üniversitesi'nden mezun (Bilgisayar Mühendisliği, 2017-2023), SOFTİTO Backend Bootcamp'te birincilik kazandı (2025) ve 7+ production projesi var. .NET Core, React.js, Java Spring Boot, PostgreSQL, Docker, Azure ve AWS ile çalışıyor. Hangi konuda bilgi almak istersiniz? İletişim: furkan@furkangul.net, +90 507 584 77 22`;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const botResponse = await generateResponse(currentInput);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: language === 'en' 
          ? 'Sorry, I cannot respond right now. Please try again later.'
          : 'Üzgünüm, şu anda yanıt veremiyorum. Lütfen daha sonra tekrar deneyin.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button - Fixed Bottom Right */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onOpen}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white rounded-full shadow-2xl z-50 flex items-center justify-center hover:shadow-indigo-500/50 transition-all"
        >
          <Icon Component={FaCommentDots} className="text-2xl" />
        </motion.button>
      )}

      {/* Chat Window - Fixed Bottom Right */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 w-96 h-[600px] bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl z-50 flex flex-col border border-indigo-500/20"
        >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon Component={FaRobot} className="text-2xl" />
                <div>
                  <h3 className="font-semibold">{t('chatbot.title')}</h3>
                  <p className="text-sm opacity-90">{t('chatbot.subtitle')}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <Icon Component={FaTimes} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-900/50">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.sender === 'bot' && (
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon Component={FaRobot} className="text-white text-sm" />
                    </div>
                  )}

                  <div
                    className={`max-w-[75%] p-3 rounded-lg ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'bg-slate-700 text-gray-100'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.text}</p>
                    <p className={`text-xs mt-1 ${
                      message.sender === 'user' ? 'text-indigo-200' : 'text-gray-400'
                    }`}>
                      {message.timestamp.toLocaleTimeString(language === 'tr' ? 'tr-TR' : 'en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>

                  {message.sender === 'user' && (
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon Component={FaUser} className="text-white text-sm" />
                    </div>
                  )}
                </motion.div>
              ))}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3 justify-start"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Icon Component={FaRobot} className="text-white text-sm" />
                  </div>
                  <div className="bg-slate-700 p-3 rounded-lg">
                    <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-purple-500/20 bg-slate-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={language === 'tr' ? 'Mesajınızı yazın...' : 'Type your message...'}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white border border-purple-500/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white p-2 rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon Component={FaPaperPlane} />
                </button>
              </div>
            </div>
        </motion.div>
      )}
    </>
  );
};

export default ChatBot;